import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import PaymentModal from './PaymentModal'
import { apiFetch } from '../../api.js'
import { apiGetCustomerInstallments } from '../../api/payments.api.js'

function cls(...xs) {
  return xs.filter(Boolean).join(' ')
}

function fmtMoney(v) {
  const n = Number(v || 0)
  return (Number.isFinite(n) ? n : 0).toLocaleString('fr-FR') + ' FCFA'
}

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

function badgeTone(status) {
  const s = String(status || '').toLowerCase()
  if (s === 'paid') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (s === 'partial') return 'bg-amber-50 text-amber-800 border-amber-200'
  return 'bg-surface-primary text-content-primary border-border'
}

export default function InstallmentsPage() {
  const q = useQuery()
  const planId = q.get('plan_id') || ''
  const customerUid = q.get('customer_uid') || ''

  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [items, setItems] = useState([])
  const [plan, setPlan] = useState(null)

  const [modal, setModal] = useState({ open: false, installment: null })

  async function load() {
    if (!planId && !customerUid) {
      setErr('plan_id ou customer_uid manquant dans l’URL')
      setLoading(false)
      return
    }

    setLoading(true)
    setErr('')

    try {
      if (planId) {
        const data = await apiFetch(`/api/payments/plans/${planId}/installments`)
        const inst = Array.isArray(data) ? data : (data?.installments || [])
        setItems(inst)
        setPlan(data?.plan || null)
      } else {
        const data = await apiGetCustomerInstallments(customerUid)
        const inst = Array.isArray(data) ? data : (data?.installments || [])
        setItems(inst)
        setPlan(null)
      }
    } catch (e) {
      console.error('[installments.load]', e)
      setErr(e?.message || 'Erreur chargement échéances')
      setItems([])
      setPlan(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, customerUid])

  const totals = useMemo(() => {
    const arr = Array.isArray(items) ? items : []
    const total = arr.reduce((a, x) => a + Number(x?.amount || 0), 0)
    const paid = arr.reduce((a, x) => a + Number(x?.amount_paid || x?.amountPaid || 0), 0)
    const due = arr.reduce((a, x) => a + Number(x?.amount_due || x?.amountDue || 0), 0)
    return {
      total: Number.isFinite(total) ? total : 0,
      paid: Number.isFinite(paid) ? paid : 0,
      due: Number.isFinite(due) ? due : 0,
    }
  }, [items])

  const openPay = (it) => {
    setModal({ open: true, installment: it })
  }

  const onPaid = (updated) => {
    // remplace l’échéance mise à jour dans la liste
    const updId = updated?.installment_id || updated?.installmentId || updated?.id
    if (!updId) return load()

    setItems(prev =>
      prev.map(x => {
        const xid = x?.installment_id || x?.installmentId || x?.id
        return xid === updId ? { ...x, ...updated } : x
      })
    )
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Échéancier</h1>
              <p className="text-sm text-slate-500 mt-1">
                {planId ? (
                  <>
                    Plan: <span className="font-mono text-xs">{planId || '—'}</span>
                  </>
                ) : (
                  <>
                    Client: <span className="font-mono text-xs">{customerUid || '—'}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={load}
                className="px-3 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-semibold"
              >
                ↻ Rafraîchir
              </button>
              {customerUid ? (
                <Link
                  to="/customers"
                  className="px-3 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-semibold"
                >
                  ← Retour clients
                </Link>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <div className="text-xs text-slate-500">Total plan</div>
              <div className="text-xl font-extrabold text-slate-900">{fmtMoney(totals.total)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <div className="text-xs text-slate-500">Déjà payé</div>
              <div className="text-xl font-extrabold text-emerald-700">{fmtMoney(totals.paid)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <div className="text-xs text-slate-500">Reste à payer</div>
              <div className="text-xl font-extrabold text-rose-700">{fmtMoney(totals.due)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-6 space-y-4">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-slate-500 text-center">
            Chargement…
          </div>
        ) : err ? (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-rose-700">
            {err}
            <div className="text-xs mt-2 text-rose-700/80">
              Vérifie que l’URL contient `plan_id` ou `customer_uid`.
            </div>
          </div>
        ) : !items.length ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-slate-500 text-center">
            Aucune échéance pour ce plan.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y shadow-sm">
            {items.map((it) => {
              const id = it?.installment_id || it?.installmentId || it?.id
              const dueDate = it?.due_date || it?.dueDate
              const status = it?.status || it?.raw_status || 'pending'
              const amount = Number(it?.amount || 0)
              const amountDue = Number(it?.amount_due ?? it?.amountDue ?? 0)
              const orderExternal = it?.order_external_id || it?.orderExternalId || null

              return (
                <div key={id || Math.random()} className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {dueDate ? new Date(dueDate).toLocaleDateString('fr-FR') : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Montant: {fmtMoney(amount)} • Reste: {fmtMoney(amountDue)}
                      {orderExternal ? ` • Vente: ${orderExternal}` : ''}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={cls('px-2 py-1 text-xs rounded-full border', badgeTone(status))}>
                      {String(status).toUpperCase()}
                    </span>

                    <button
                      type="button"
                      onClick={() => openPay(it)}
                      disabled={!amountDue || amountDue <= 0}
                      className={cls(
                        'px-3 py-2 rounded-xl font-semibold text-sm',
                        amountDue > 0
                          ? 'bg-slate-900 text-white hover:bg-slate-950'
                          : 'border border-slate-200 text-slate-400 cursor-not-allowed'
                      )}
                    >
                      Encaisser
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <PaymentModal
          open={modal.open}
          installment={modal.installment}
          onClose={() => setModal({ open: false, installment: null })}
          onPaid={onPaid}
        />
      </div>
    </div>
  )
}
